import React from 'react';
import styles from './SoftwareAssistance.module.css';

const SoftwareAssistance = () => {
  const handleCTAClick = (action) => {
    console.log(`${action} clicked`);
    // Add your navigation/modal logic here
  };

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroHeadline}>
            Solve Software Issues Instantly with AI
          </h1>
          <p className={styles.heroSubtext}>
            Toxbox AI provides smart software support, automating troubleshooting, 
            updates, and fixes to keep your applications running flawlessly.
          </p>
          <div className={styles.ctaButtons}>
            <button 
              className={`${styles.ctaButton} ${styles.ctaPrimary}`}
              onClick={() => handleCTAClick('Get Started')}
            >
              Get Started
            </button>
            <button 
              className={`${styles.ctaButton} ${styles.ctaSecondary}`}
              onClick={() => handleCTAClick('Request a Demo')}
            >
              Request a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Why AI-Powered Software Support */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Why AI-Powered Software Support?</h2>
          <div className={styles.featuresGrid}>
            <div className={styles.featureCard}>
              <h3>24/7 Availability</h3>
              <p>Immediate assistance anytime for software issues.</p>
            </div>
            <div className={styles.featureCard}>
              <h3>Automated Fixes</h3>
              <p>AI detects errors and applies solutions instantly.</p>
            </div>
            <div className={styles.featureCard}>
              <h3>Reduce Downtime</h3>
              <p>Keep your software running smoothly.</p>
            </div>
            <div className={styles.featureCard}>
              <h3>Seamless Integration</h3>
              <p>Works with desktop, web, and mobile applications.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Key Features</h2>
          <div className={styles.featuresList}>
            <div className={styles.featureItem}>
              <h4>Automated Troubleshooting</h4>
              <p>AI identifies bugs, crashes, and compatibility issues.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>Patch & Update Management</h4>
              <p>Keep all applications updated automatically.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>Multi-Channel Support</h4>
              <p>Access via chat, email, or web portal.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>Knowledge Base Integration</h4>
              <p>Instant guidance from software manuals and documentation.</p>
            </div>
            <div className={styles.featureItem}>
              <h4>Analytics & Insights</h4>
              <p>Monitor software performance and recurring issues.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={`${styles.section} ${styles.howItWorksSection}`}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>How It Works</h2>
          <div className={styles.stepsContainer}>
            {[
              "User reports a software issue via Toxbox AI portal or chat.",
              "AI analyzes the software logs and error messages.",
              "AI applies automated fixes or recommends corrective actions.",
              "If unresolved, the issue is escalated to a human technician.",
              "Users receive real-time updates and a resolution report."
            ].map((step, index) => (
              <div key={index} className={styles.stepItem}>
                <div className={styles.stepNumber}>{index + 1}</div>
                <p className={styles.stepText}>{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Benefits</h2>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitCard}>
              <h4>Immediate Support</h4>
              <p>No waiting for IT helpdesk.</p>
            </div>
            <div className={styles.benefitCard}>
              <h4>Consistent Solutions</h4>
              <p>AI applies standardized fixes across users.</p>
            </div>
            <div className={styles.benefitCard}>
              <h4>Reduce Support Costs</h4>
              <p>Automate repetitive software troubleshooting.</p>
            </div>
            <div className={styles.benefitCard}>
              <h4>Data-Driven Decisions</h4>
              <p>Insights into recurring software issues.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Software */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Supported Software</h2>
          <div className={styles.supportGrid}>
            <div className={styles.supportCategory}>
              <h4>Operating Systems</h4>
              <p>Windows, macOS, Linux</p>
            </div>
            <div className={styles.supportCategory}>
              <h4>Applications</h4>
              <p>Productivity, CRM, ERP, custom enterprise software</p>
            </div>
            <div className={styles.supportCategory}>
              <h4>Web & Cloud Apps</h4>
              <p>SaaS platforms, collaborative tools, and online services</p>
            </div>
            <div className={styles.supportCategory}>
              <h4>Mobile Apps</h4>
              <p>iOS and Android troubleshooting</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Insights & Recommendations */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>AI Insights & Recommendations</h2>
          <div className={styles.insightsList}>
            <div className={styles.insightItem}>
              <p>Detect recurring software errors and bug patterns.</p>
            </div>
            <div className={styles.insightItem}>
              <p>Suggest preventive actions and optimization tips.</p>
            </div>
            <div className={styles.insightItem}>
              <p>Provide actionable recommendations to improve software performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Human Handoff Flexibility */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Human Handoff Flexibility</h2>
          <div className={styles.handoffList}>
            <div className={styles.handoffItem}>
              <p>Escalate complex software issues to human support.</p>
            </div>
            <div className={styles.handoffItem}>
              <p>Provide technicians with full diagnostic history.</p>
            </div>
            <div className={styles.handoffItem}>
              <p>Ensure faster, accurate resolution for advanced problems.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Industry Applications</h2>
          <div className={styles.industryGrid}>
            <div className={styles.industryCard}>
              <h4>Enterprise IT</h4>
              <p>Software troubleshooting for employees and systems.</p>
            </div>
            <div className={styles.industryCard}>
              <h4>Tech Companies</h4>
              <p>Rapid bug fixes for applications.</p>
            </div>
            <div className={styles.industryCard}>
              <h4>Education</h4>
              <p>Support for learning management systems and labs.</p>
            </div>
            <div className={styles.industryCard}>
              <h4>Healthcare</h4>
              <p>Software troubleshooting for medical applications.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className={`${styles.section} ${styles.testimonialsSection}`}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Loved by IT Teams & Enterprises</h2>
          <div className={styles.testimonialsGrid}>
            <div className={styles.testimonialCard}>
              <blockquote>
                "Toxbox AI fixed our software issues in minutes, not hours."
              </blockquote>
              <cite>– IT Manager</cite>
            </div>
            <div className={styles.testimonialCard}>
              <blockquote>
                "Automated troubleshooting saved us countless support hours."
              </blockquote>
              <cite>– Tech Lead</cite>
            </div>
            <div className={styles.testimonialCard}>
              <blockquote>
                "We now have a clear view of recurring software problems and solutions."
              </blockquote>
              <cite>– Enterprise Client</cite>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className={styles.ctaSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.ctaHeadline}>Simplify Software Support with AI</h2>
          <p className={styles.ctaSubtext}>
            Automate troubleshooting, updates, and issue resolution for all your software.
          </p>
          <div className={styles.ctaButtons}>
            <button 
              className={`${styles.ctaButton} ${styles.ctaPrimary}`}
              onClick={() => handleCTAClick('Request a Demo')}
            >
              Request a Demo
            </button>
            <button 
              className={`${styles.ctaButton} ${styles.ctaSecondary}`}
              onClick={() => handleCTAClick('Get Started Now')}
            >
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Safe & Secure Software Assistance</h2>
          <div className={styles.securityList}>
            <div className={styles.securityItem}>
              <p>End-to-end encrypted software diagnostics and data handling</p>
            </div>
            <div className={styles.securityItem}>
              <p>GDPR & IT compliance ready</p>
            </div>
            <div className={styles.securityItem}>
              <p>Trusted by enterprises globally</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoftwareAssistance;
